import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Lock, KeyRound, Eye, Home, HandCoins, BadgeCheck, Flag } from "lucide-react";
import api from "@/lib/api";
import { PageLoading } from "@/components/common";

const TIPS = [
  { icon: KeyRound, title: "Never share passwords or OTP codes", desc: "Mtaa staff and legitimate users will never ask for your password or verification code." },
  { icon: Eye, title: "Verify expensive items in person", desc: "For high-value items, inspect before you pay. Use our 'Verify you have this item' check where offered." },
  { icon: Home, title: "Inspect accommodation before paying", desc: "Never transfer money for a rental before independently verifying the property and the person." },
  { icon: HandCoins, title: "Be cautious of pressure to pay", desc: "Urgency, upfront deposits, gift cards and crypto are common scam signals." },
  { icon: BadgeCheck, title: "Verify professional credentials", desc: "Ask for licences and check verification badges — a badge is evidence, not a guarantee." },
  { icon: Flag, title: "Report suspicious behaviour", desc: "Every listing and message can be reported in about 10 seconds. Reporting keeps Mtaa safe." },
];

export function Safety() {
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="rounded-3xl border border-border bg-card p-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-green/10"><ShieldCheck className="h-6 w-6 text-brand-green" /></div>
        <h1 className="mt-3 font-display text-2xl font-bold tracking-tight text-foreground">Mtaa Safety Centre</h1>
        <p className="mt-1 text-sm text-muted-foreground">Mtaa builds layered trust with phone verification, risk signals, reporting, blocking and human review. No platform is scam-proof — stay alert and use these tips.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {TIPS.map((t) => { const Icon = t.icon; return (
          <div key={t.title} className="rounded-2xl border border-border bg-card p-4">
            <Icon className="h-6 w-6 text-primary" />
            <h3 className="mt-2 font-display font-semibold">{t.title}</h3>
            <p className="text-sm text-muted-foreground">{t.desc}</p>
          </div>
        ); })}
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display font-semibold">Legal & policies</h2>
        <div className="mt-2 flex flex-wrap gap-2 text-sm">
          {[["terms", "Terms of Use"], ["privacy", "Privacy Policy"], ["community", "Community Guidelines"], ["marketplace-rules", "Marketplace Rules"], ["refund", "Refund Policy"], ["advertising", "Advertising Terms"]].map(([k, l]) => (
            <a key={k} href={`/legal/${k}`} className="rounded-full border border-border px-3 py-1 hover:border-primary hover:text-primary" data-testid={`legal-link-${k}`}>{l}</a>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Legal() {
  const { doc } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/legal/${doc}`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [doc]);
  if (data === null) return <PageLoading />;
  if (data === false) return <div className="py-16 text-center text-muted-foreground">Document not found.</div>;
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="font-display text-2xl font-bold tracking-tight">{data.title}</h1>
      <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200"><Lock className="mt-0.5 h-4 w-4 shrink-0" />{data.notice}</div>
      <p className="text-sm leading-relaxed text-foreground/80">{data.body}</p>
    </div>
  );
}
